import { useMemo, useState } from 'react';
import SiteLayout from '@/components/feature/SiteLayout';
import PageHeader from '@/components/feature/PageHeader';
import ContentCard from '@/components/feature/ContentCard';
import Pagination from '@/components/base/Pagination';
import useSEO from '@/hooks/useSEO';
import { getStoriesByTime, getStoriesByWeight, paginate, totalPages } from '@/lib/content';

const PAGE_SIZE = 6;

export default function StoryPage() {
  useSEO({
    title: '剧情介绍与分集剧情 - 我的朋友他的妻子',
    description:
      '按时间顺序整理《我的朋友他的妻子》的剧情节点，从雨夜重逢到杀青告别，包含关键场景、人物关系变化与剧情摘要，可切换按重要程度排序。',
    keywords: '我的朋友他的妻子剧情,剧情介绍,分集剧情,剧情分析',
  });

  const [sort, setSort] = useState<'time' | 'weight'>('time');
  const [page, setPage] = useState(1);

  const sorted = useMemo(
    () => (sort === 'time' ? getStoriesByTime() : getStoriesByWeight()),
    [sort],
  );
  const pages = totalPages(sorted.length, PAGE_SIZE);
  const visible = paginate(sorted, page, PAGE_SIZE);

  const changeSort = (next: 'time' | 'weight') => {
    setSort(next);
    setPage(1);
  };

  return (
    <SiteLayout>
      <PageHeader
        eyebrow="STORY GUIDE"
        title="剧情介绍"
        description="《我的朋友他的妻子》共 12 集。这里把零散的片段按时间线拼起来，标注每一幕的关键场景与三人关系的位移，帮助你理清“他为什么不说”这件事。"
        crumbs={[{ label: '首页', to: '/' }, { label: '剧情介绍' }]}
        image={sorted[0]?.image}
      >
        <div className="flex shrink-0 items-center gap-1 rounded-full border border-background-300/70 bg-background-100/70 p-1">
          <button
            type="button"
            onClick={() => changeSort('time')}
            className={`whitespace-nowrap rounded-full px-4 py-2 text-xs transition-colors cursor-pointer md:text-sm ${
              sort === 'time'
                ? 'bg-primary-500 text-background-50'
                : 'text-foreground-400 hover:text-foreground-950'
            }`}
          >
            按时间顺序
          </button>
          <button
            type="button"
            onClick={() => changeSort('weight')}
            className={`whitespace-nowrap rounded-full px-4 py-2 text-xs transition-colors cursor-pointer md:text-sm ${
              sort === 'weight'
                ? 'bg-primary-500 text-background-50'
                : 'text-foreground-400 hover:text-foreground-950'
            }`}
          >
            按重要程度
          </button>
        </div>
      </PageHeader>

      <section className="w-full px-4 py-10 md:px-8 md:py-14 lg:px-12">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-foreground-400">
            共 <span className="text-primary-400">{sorted.length}</span> 个剧情节点 · 当前
            <span className="text-foreground-900"> {sort === 'time' ? '按时间顺序' : '按重要程度'}</span> 排列
          </p>
          <span className="font-label text-xs tracking-[0.25em] text-foreground-600">
            PAGE {page} / {pages}
          </span>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-2">
          {visible.map((item, index) => (
            <ContentCard key={item.id} item={item} variant="wide" index={index} />
          ))}
        </div>

        <div className="mt-10">
          <Pagination page={page} total={pages} onChange={setPage} />
        </div>
      </section>
    </SiteLayout>
  );
}